import { Location } from "history";
import { call, getContext, put, select, takeEvery } from "redux-saga/effects";

import {
  getLocaleWithScript,
  NavigationActions,
  navigationActions,
  RouteConfigMatch,
  SagaContextKey,
} from "@gamedev-web/gamedev-sdk-core";
import { isUhfConfigGeneratorFn, uhfActions, UhfConfigGeneratorFn } from "@gamedev-web/gamedev-uhf";
import { toError } from "@gaming/data-validation";
import { ERT } from "@gaming/extract-redux-saga-return-type";

import { getPageId } from "../../helpers";
import { PartnerConfigManager } from "../../partners/PartnerConfigManager";
import { getMatchedRouteConfigSagaEffect } from "../../routes";
import { RootState } from "../reducers/rootReducer";

function* loadPageDataSaga(
  routeMatch: RouteConfigMatch<RootState> | undefined,
  newLocation: Location,
  oldLocation?: Location,
  forceRefresh?: boolean
) {
  if (!routeMatch || !routeMatch.lazyConfig) {
    return;
  }

  const lazyConfig: ERT<typeof routeMatch.lazyConfig> = yield call(routeMatch.lazyConfig);

  if (!lazyConfig.fetchDataSaga) {
    return;
  }

  if (
    !routeMatch.requiresNewRequest ||
    routeMatch.requiresNewRequest(routeMatch.path, newLocation, oldLocation) ||
    forceRefresh
  ) {
    const locale: ERT<typeof getLocaleWithScript> = yield select(getLocaleWithScript);
    const pageId = getPageId(routeMatch, locale);

    yield put(navigationActions.pageDataRequest.start(pageId));

    try {
      yield call(lazyConfig.fetchDataSaga, routeMatch, newLocation);
      yield put(navigationActions.pageDataRequest.success(pageId));

      let config = routeMatch?.uhfConfig;
      if (isUhfConfigGeneratorFn(config)) {
        const resultingConfig: ERT<UhfConfigGeneratorFn> = yield call(config);
        config = resultingConfig;
      }
    } catch (e) {
      const error = toError(e);
      console.error(`Failed to fetch page data for path: ${newLocation.pathname}`, error);
      yield put(navigationActions.pageDataRequest.error(pageId, error));
    }
  }
}

export function* navigationSaga(
  newLocation: Location,
  oldLocation?: Location,
  forceRefresh?: boolean
) {
  const routeMatch: RouteConfigMatch<RootState> | undefined = yield getMatchedRouteConfigSagaEffect(
    newLocation.pathname
  );
  const partnerConfigManager = (yield getContext(SagaContextKey.partners)) as PartnerConfigManager;
  // Load the config and route if we navigated to a new config.
  yield call(partnerConfigManager.loadConfigurationForPath, newLocation.pathname);
  yield call(loadPageDataSaga, routeMatch, newLocation, oldLocation, forceRefresh);
}

function* navigationSagaHandler(action: ReturnType<typeof navigationActions.navigateToPath>) {
  const routeMatch: RouteConfigMatch<RootState> | undefined = yield getMatchedRouteConfigSagaEffect(
    action.newLocation.pathname
  );

  if (routeMatch?.isServerOnly) {
    // Links from page would get captured by the CaptureLinkClicks handler
    // Ending up here means we navigated in a way that we can't capture before navigating
    // (e.g. via browser back button) or didn't handle. This is the edge case.
    // Assumption that in-page navigation (hash, search param) does not need reload or refetch of data
    if (
      action.oldLocation?.pathname?.toLowerCase() !== action.newLocation?.pathname?.toLowerCase()
    ) {
      globalThis.location.reload();
    }
  } else {
    yield call(navigationSaga, action.newLocation, action.oldLocation, action.forceRefresh);
  }
}

export function* setUhfModeSaga(path: string) {
  const routeMatch: RouteConfigMatch<RootState> | undefined =
    yield getMatchedRouteConfigSagaEffect(path);

  let config = routeMatch?.uhfConfig;
  if (isUhfConfigGeneratorFn(config)) {
    const resultingConfig: ERT<UhfConfigGeneratorFn> = yield call(config);
    config = resultingConfig;
  }

  const headerMode = config?.headerMode;
  if (headerMode) {
    yield put(uhfActions.setHeaderMode(headerMode));
  } else {
    yield put(uhfActions.resetHeaderMode());
  }

  const footerMode = config?.footerMode;
  if (footerMode) {
    yield put(uhfActions.setFooterMode(footerMode));
  } else {
    yield put(uhfActions.resetFooterMode());
  }
}

function* setUhfModeHandler(action: ReturnType<typeof navigationActions.navigateToPath>) {
  yield call(setUhfModeSaga, action.newLocation.pathname);
}

export function* navigationSagas() {
  yield takeEvery(NavigationActions.LOCATION_CHANGE, navigationSagaHandler);
  yield takeEvery(NavigationActions.LOCATION_CHANGE, setUhfModeHandler);
}
